import Button from 'assets/components/button/Button';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import ProgressWindow, { addProgressToWindow, ProgressKey } from 'assets/components/progressWindow/ProgressWindow';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { date, defaultDateTimeFormat } from 'assets/utils/data/Date';
import iterateWithProgress, { ProgressInfo } from 'assets/utils/data/Iterations';
import { HttpStatus } from 'config/Api.Config';
import JSZip from 'jszip';
import { isEmpty } from 'lodash';
import claimJobFindApi from 'models/claims/claimJob/find/Api.ClaimJob.Find';
import mediaFileGetBlobApi from 'models/core/mediaFile/getFile/Api.MediaFile.GetFile';
import { useEffect, useState } from 'react';

export default function ClaimDownloadDocuments({ claim, isDisabled }: { claim: Model.IClaim; isDisabled?: boolean }) {
  const { lang } = getLocales();
  const [claimJobsDocuments, setClaimJobsDocuments] = useState<Model.IClaimJobDocument[]>([]);

  const downloadDocuments = async ({
    files,
    zipName,
    onSaveProgress,
  }: {
    files: Model.IMediaFile[];
    zipName: string;
    onSaveProgress?: (key: ProgressKey, progressInfo: ProgressInfo) => void;
  }) => {
    if (isEmpty(files)) return addLog({ warning: lang.notFound });

    try {
      const zip = new JSZip();
      const fileNameCount = {};

      await iterateWithProgress(
        files,
        async (file) => {
          const response = await mediaFileGetBlobApi({ id: file.id });
          const arrayBuffer = await response.payload.arrayBuffer();

          if (response.status === HttpStatus.ok) {
            const match = file.name.match(/^(.*?)(\.[^.]+)?$/);
            const baseName = match[1];
            const extension = match[2] || '';

            const count = fileNameCount[baseName] ?? 0;
            const uniqueName = count > 0 ? `${baseName}(${count})${extension}` : file.name;

            fileNameCount[baseName] = count + 1;

            zip.file(uniqueName, arrayBuffer);
          }
        },
        (p) => onSaveProgress('download', p)
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `${zipName}-${date().format(defaultDateTimeFormat)}.zip`;
      link.click();

      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('Error while zipping files:', error);
    }
  };
  useEffect(() => {
    (async () => {
      if (claim && !isEmpty(claim.claimJobs)) {
        const promises = claim.claimJobs.map((claimJob) => claimJobFindApi({ id: claimJob?.id }));
        const results = await Promise.all(promises);
        const validDocuments = results
          .filter((res) => res.status === HttpStatus.ok && !isEmpty(res.payload?.claimJobDocuments))
          .flatMap((res) => res.payload.claimJobDocuments);
        setClaimJobsDocuments(validDocuments);
      }
    })();
  }, [claim]);

  const claimMediaFiles = claim?.claimDocuments?.map((it) => it.mediaFile) || [];
  const claimJobsMediaFiles = claimJobsDocuments?.map((it) => it.mediaFile) || [];

  return (
    <>
      <ProgressWindow />
      <DropdownButton
        buttonBody={lang.download}
        buttonMedia={<Icon class="fas-download" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={ThemeDropdownButtonPopup}
        tabIndex={null}
        disabled={isDisabled}
        htmlElementProps={{ title: `${lang.download} ${lang.claim} ${lang.documents}` }}
        dropdownOptions={
          <>
            <Button
              onClick={downloadDocuments}
              data={{ files: claimMediaFiles, zipName: 'ClaimDocuments', onSaveProgress: addProgressToWindow }}
              htmlElementProps={{ title: lang.downloadAllClaimDocuments }}
              render={`${lang.claim} ${lang.documents}`}
              class={ThemeButtonMenuItem}
              disabled={isEmpty(claimMediaFiles)}
            />
            <Button
              onClick={downloadDocuments}
              data={{ files: claimJobsMediaFiles, zipName: 'ClaimJobsDocuments', onSaveProgress: addProgressToWindow }}
              htmlElementProps={{ title: lang.downloadAllClaimJobsDocuments }}
              render={`${lang.claimJob}s ${lang.documents}`}
              class={ThemeButtonMenuItem}
              disabled={isEmpty(claimJobsMediaFiles)}
            />
            <Button
              onClick={downloadDocuments}
              data={{
                files: [...claimMediaFiles, ...claimJobsMediaFiles],
                zipName: 'AllClaimDocuments',
                onSaveProgress: addProgressToWindow,
              }}
              htmlElementProps={{ title: lang.downloadAll }}
              render={lang.downloadAll}
              class={ThemeButtonMenuItem}
              disabled={isEmpty(claimMediaFiles) && isEmpty(claimJobsMediaFiles)}
            />
          </>
        }
      />
    </>
  );
}
